import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../api";

/**
 * Response Structure
 * 
 * {
  "statusCode": 200,
  "meta": {
    "clinicianCount": 3,
    "weekRange": "2026-04-27 to 2026-05-03",
    "monthRange": "2026-04",
    "practiceWeekUtilizationPct": 76.5,
    "practiceMonthUtilizationPct": 68.2
  },
  "body": [
    {
      "fullName": "Hannah Asfeldt",
      "week": { "bookedSlots": 12, "openSlots": 6, "utilizationPct": 66.67 },
      "month": { "bookedSlots": 48, "openSlots": 20, "utilizationPct": 70.59 }
    }
  ]
}
 */
interface ClinicianData {
    clinicianId: string;
    fullName: string;
    title: string;
    status: string;
    isActive: boolean;
    week: {
        range: string;
        bookedSlots: number;
        openSlots: number;
        totalSlots: number;
        utilizationPct: number;
    };
    month: {
        range: string;
        bookedSlots: number;
        openSlots: number;
        totalSlots: number;
        utilizationPct: number;
    };
}

interface Meta {
    clinicianCount: number;
    weekRange: string;
    monthRange: string;
    practiceWeekUtilizationPct: number;
    practiceMonthUtilizationPct: number;
}

interface AnalyticState {
    clinicianUtil: {
        meta: Meta;
        data: ClinicianData[];
    };
    isLoading: boolean;
    error: string | null;
}

const initialState: AnalyticState = {
    clinicianUtil: {
        meta: {
            clinicianCount: 0,
            weekRange: '',
            monthRange: '',
            practiceWeekUtilizationPct: 0,
            practiceMonthUtilizationPct: 0,
        },
        data: [],
    },
    isLoading: false,
    error: null,
};

export const fetchUtilizationAnalytics = createAsyncThunk(
    'analytic/fetchUtilizationAnalytics',
    async ({ practiceId, date, useLastWeek }: { practiceId: string, date: string, useLastWeek: boolean }, { rejectWithValue }) => {

        const url = `/clinician/analytics/utilization`;
        try {
            const response = await api.post(url, {
                id_practice: practiceId,
                date,
                use_last_week: useLastWeek
            }, {
                params: {
                    id_practice: practiceId,
                    date,
                    use_last_week: useLastWeek
                }
            });

            return response.data;
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch utilization analytics';
            return rejectWithValue(errorMessage);
        }
    }
);

const analyticSlice = createSlice({
    name: 'analytic',
    initialState,
    reducers: {
        resetAnalytics: (state) => {
            state.clinicianUtil = initialState.clinicianUtil;
            state.isLoading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUtilizationAnalytics.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUtilizationAnalytics.fulfilled, (state, action) => {
                state.isLoading = false;
                state.clinicianUtil = {
                    meta: action.payload?.meta ?? initialState.clinicianUtil.meta,
                    data: Array.isArray(action.payload?.body) ? action.payload.body : [],
                };
            })
            .addCase(fetchUtilizationAnalytics.rejected, (state, action) => {
                state.isLoading = false;
                state.error = (action.payload as string) || action.error.message || 'An unknown error occurred';
            });
    },
});

export const { resetAnalytics } = analyticSlice.actions;

export default analyticSlice.reducer;
